import React from "react";
import { Search } from "lucide-react";
import { useGlobalSearch } from "./useGlobalSearch";
import { cn } from "@/lib/utils";

interface GlobalSearchTriggerProps {
  className?: string;
  variant?: "sidebar" | "compact" | "icon" | "topbar";
}

export const GlobalSearchTrigger: React.FC<GlobalSearchTriggerProps> = ({
  className,
  variant = "topbar",
}) => {
  const { openSearch } = useGlobalSearch();

  if (variant === "icon") {
    return (
      <button
        type="button"
        onClick={() => openSearch()}
        className={cn(
          "w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer",
          className,
        )}
        title="Pesquisa Geral (Ctrl+K)"
      >
        <Search className="w-4 h-4" />
      </button>
    );
  }

  if (variant === "compact") {
    return (
      <button
        type="button"
        onClick={() => openSearch()}
        className={cn(
          "flex items-center gap-2 px-2.5 py-1.5 text-xs text-muted-foreground bg-muted/50 hover:bg-muted hover:text-foreground border border-border/60 rounded-lg transition-all cursor-pointer",
          className,
        )}
        title="Pesquisa Geral (Ctrl+K)"
      >
        <Search className="w-3.5 h-3.5" />
        <span>Buscar...</span>
        <kbd className="hidden sm:inline-flex h-4 select-none items-center rounded border border-border bg-background px-1 font-mono text-[9px] text-muted-foreground">
          Ctrl K
        </kbd>
      </button>
    );
  }

  if (variant === "topbar") {
    return (
      <button
        type="button"
        onClick={() => openSearch()}
        className={cn(
          "w-full max-w-md lg:max-w-xl h-10 flex items-center justify-between px-3.5 text-xs text-muted-foreground bg-muted/40 hover:bg-muted/70 hover:text-foreground border border-border/70 hover:border-primary/40 rounded-lg transition-all duration-150 shadow-2xs group cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/20",
          className,
        )}
        title="Pesquisa Geral (Ctrl+K)"
      >
        <div className="flex items-center gap-2.5 truncate">
          <Search className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
          <span className="truncate text-xs font-normal text-muted-foreground group-hover:text-foreground transition-colors">
            Pesquisar contatos, empresas, negócios...
          </span>
        </div>
        <kbd className="pointer-events-none hidden sm:inline-flex h-5 select-none items-center gap-1 rounded border border-border/80 bg-background/80 px-1.5 font-mono text-[10px] font-medium text-muted-foreground shadow-2xs shrink-0">
          Ctrl K
        </kbd>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => openSearch()}
      className={cn(
        "w-full flex items-center justify-between px-3 py-2 text-xs text-muted-foreground bg-muted/40 hover:bg-muted/80 hover:text-foreground border border-border/70 rounded-lg transition-all shadow-xs group cursor-pointer",
        className,
      )}
      title="Pesquisa Geral (Ctrl+K)"
    >
      <div className="flex items-center gap-2 truncate">
        <Search className="w-3.5 h-3.5 text-muted-foreground group-hover:text-primary transition-colors" />
        <span className="truncate font-medium">Pesquisa geral...</span>
      </div>
      <kbd className="pointer-events-none hidden sm:inline-flex h-5 select-none items-center gap-1 rounded border border-border/80 bg-background/80 px-1.5 font-mono text-[10px] font-medium text-muted-foreground shadow-2xs">
        Ctrl K
      </kbd>
    </button>
  );
};
